import React from "react";
import { motion } from "framer-motion";
import { Reveal, SectionLabel, renderPh, scrollToId, Ph, SectionBg, Accordion } from "./fx";
import { HostPanel } from "./HostPanel";
import { PH } from "./copy";

const BOOKING_WIDGET_URL = "https://link.smallbusinessautomator.com/widget/booking/r7SmyGC7c18FN9z1W4vS";

const BookCTA = ({ label, testId }) => (
  <button data-testid={testId} data-cta onClick={() => scrollToId("book")} className="btn-accent px-8 py-4 text-sm tracking-widest uppercase">
    {label}
  </button>
);

export const RManifesto = ({ rt, lang }) => (
  <section className="relative overflow-hidden py-28 md:py-36 px-6 md:px-12" data-testid="replay-manifesto-section">
    <SectionBg img="/img/home2.jpg" />
    <div className="relative max-w-[1000px] mx-auto">
      <SectionLabel>{rt.manifesto.label}</SectionLabel>
      <h2 className="h1-display text-[clamp(36px,5vw,64px)]" style={{ lineHeight: 0.97 }}>
        {rt.manifesto.lines.map((line, li) => (
          <span key={`${lang}-${li}`} className={`block ${line.c === "a" ? "accent" : ""}`} style={line.c === "m" ? { opacity: 0.28 } : {}}>
            {line.t.split(" ").map((w, wi) => (
              <motion.span
                key={wi}
                className="inline-block mr-[0.25em]"
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.55, delay: (li * 3 + wi) * 0.04, ease: [0.22, 1, 0.36, 1] }}
              >
                {w}
              </motion.span>
            ))}
          </span>
        ))}
      </h2>
      <Reveal delay={0.2}>
        <p className="mt-10 text-base text-white/55 max-w-[560px]" style={{ lineHeight: 1.75 }}>{rt.manifesto.body}</p>
        <button data-testid="replay-manifesto-cta" data-cta onClick={() => scrollToId("book")} className="btn-outline mt-8 px-8 py-4 text-sm uppercase">
          {rt.manifesto.cta}
        </button>
      </Reveal>
    </div>
  </section>
);

export const RMethod = ({ rt }) => (
  <section id="method" className="py-20 border-y border-white/10" data-testid="replay-method-section">
    <div className="max-w-[1200px] mx-auto px-6">
      <Reveal><SectionLabel>{rt.method.label}</SectionLabel></Reveal>
      <div className="grid md:grid-cols-3">
        {rt.method.steps.map((s, i) => (
          <Reveal key={i} delay={i * 0.1} className={`lift-card relative px-8 py-10 ${i > 0 ? "md:border-l border-white/10" : ""}`}>
            <span className="mono absolute top-2 right-4 text-[110px] font-black leading-none select-none" style={{ opacity: 0.04 }}>{s.n}</span>
            <div className="mono accent text-[10px] tracking-[0.3em] mb-3">{s.n}</div>
            <div className="font-black uppercase text-xl mb-3">{s.t}</div>
            <p className="text-white/45 text-sm leading-relaxed">{s.d}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const RStack = ({ rt }) => (
  <section className="relative overflow-hidden py-24 md:py-32 px-6 md:px-12" data-testid="replay-stack-section">
    <SectionBg img="/img/home3.jpg" />
    <div className="relative max-w-[760px] mx-auto">
      <Reveal>
        <SectionLabel>{rt.stack.label}</SectionLabel>
        <h2 className="h1-display text-3xl md:text-5xl mb-12">{rt.stack.h2}</h2>
      </Reveal>
      <div className="glass-card p-8 md:p-10">
        <ul className="space-y-5">
          {rt.stack.items.map((it, i) => (
            <Reveal key={i} delay={i * 0.12}>
              <li className="flex gap-4 items-start" data-testid={`stack-item-${i + 1}`}>
                <span className="accent font-black text-lg shrink-0">✓</span>
                <span className="text-white/75 text-[15px] leading-relaxed">{it}</span>
              </li>
            </Reveal>
          ))}
        </ul>
        <Reveal delay={0.6}>
          <div className="h-px bg-white/10 my-8" />
          <p className="mono accent text-xs tracking-[0.15em] mb-8">{rt.stack.reveal}</p>
          <BookCTA label={rt.cta} testId="stack-cta" />
        </Reveal>
      </div>
    </div>
  </section>
);

export const RGuarantee = ({ rt }) => (
  <section className="py-24 md:py-32 px-6" data-testid="replay-guarantee-section">
    <div className="max-w-[760px] mx-auto">
      <Reveal>
        <div className="text-center"><SectionLabel>{rt.guarantee.label}</SectionLabel></div>
        <div className="border border-[#4D9FFF] p-8 md:p-12 text-center lift-card" data-testid="guarantee-card">
          <h2 className="h1-display text-2xl md:text-4xl mb-6 accent">{rt.guarantee.title}</h2>
          <div className="text-sm leading-relaxed mb-4">{renderPh(rt.guarantee.ph)}</div>
          <p className="text-white/50 italic text-[15px] leading-relaxed">{rt.guarantee.draft}</p>
        </div>
        <p className="mono text-[10px] tracking-[0.1em] text-white/30 text-center mt-4">{rt.guarantee.smallPrint}</p>
      </Reveal>
    </div>
  </section>
);

export const RHost = ({ rt, t }) => (
  <section id="host" className="relative overflow-hidden py-24 md:py-32 px-6 md:px-12" style={{ background: "rgba(255,255,255,0.015)" }} data-testid="replay-host-section">
    <div className="relative max-w-[1200px] mx-auto">
      <Reveal><SectionLabel>{rt.hostLabel}</SectionLabel></Reveal>
      <div className="grid md:grid-cols-[minmax(0,420px)_1fr] gap-12 md:gap-16 items-start">
        <Reveal><HostPanel /></Reveal>
        <Reveal delay={0.15}>
          <h2 className="h1-display text-3xl md:text-[44px] mb-6">{t.host.h2}</h2>
          <div className="h-px bg-[#4D9FFF] w-24 mb-8" />
          <div className="space-y-5 text-white/55 text-[15px] leading-[1.8] mb-10">
            {t.host.bio.map((p, i) => <p key={i}>{p}</p>)}
          </div>
          <div className="grid grid-cols-2 gap-px bg-white/10 border border-white/10 mb-10" data-testid="replay-host-credential-grid">
            {t.host.creds.map((c, i) => (
              <div key={i} className="bg-[#123059] p-5 lift-card">
                <div className="mono text-[9px] tracking-[0.3em] text-white/30 mb-2">{c.l}</div>
                <div className="text-sm font-bold">{c.ph ? <Ph>{c.v}</Ph> : c.v}</div>
              </div>
            ))}
          </div>
          <BookCTA label={rt.cta} testId="replay-host-cta" />
        </Reveal>
      </div>
    </div>
  </section>
);

export const RObjections = ({ rt }) => (
  <section className="relative overflow-hidden py-24 md:py-32 px-6 md:px-12" data-testid="replay-objections-section">
    <SectionBg img="/img/home4.jpg" />
    <div className="relative max-w-[1200px] mx-auto grid md:grid-cols-2 gap-14 md:gap-20">
      <Reveal>
        <SectionLabel>{rt.objections.label}</SectionLabel>
        <h2 className="text-3xl md:text-4xl font-black mb-8 leading-tight">{rt.objections.h2}</h2>
        {rt.objections.pains.map((p, i) => (
          <p key={i} className="text-white/50 text-[15px] leading-[1.85] mb-5">{p}</p>
        ))}
      </Reveal>
      <Reveal delay={0.15}>
        <div className="mono accent text-xs tracking-[0.25em] mb-8">{rt.objections.changesLabel}</div>
        <ul className="space-y-6 mb-10">
          {rt.objections.bullets.map((b, i) => (
            <li key={i} className="flex gap-4 items-start" data-testid={`objection-bullet-${i + 1}`}>
              <span className="accent font-black text-xl shrink-0">→</span>
              <span className="text-white/75 text-base leading-relaxed">{b}</span>
            </li>
          ))}
        </ul>
        <BookCTA label={rt.cta} testId="objections-cta" />
      </Reveal>
    </div>
  </section>
);

export const RFaq = ({ rt }) => (
  <section id="faq" className="py-24 md:py-32 px-6 md:px-12" data-testid="replay-faq-section">
    <div className="max-w-[900px] mx-auto">
      <Reveal>
        <SectionLabel>{rt.faq.label}</SectionLabel>
        <h2 className="h1-display text-3xl md:text-5xl mb-12">{rt.faq.h2}</h2>
      </Reveal>
      <Reveal delay={0.1}>
        <Accordion items={rt.faq.items} testPrefix="replay-faq" />
      </Reveal>
    </div>
  </section>
);

export const RBooking = ({ rt, lang, watchPctRef }) => (
  <section id="book" className="relative overflow-hidden py-24 md:py-36 px-6 md:px-12 grid-bg" data-testid="replay-booking-section">
    <SectionBg img="/img/home1.jpg" opacity={0.12} />
    <div
      className="absolute left-0 top-1/2 -translate-y-1/2 w-[700px] h-[700px] pointer-events-none"
      style={{ background: "radial-gradient(circle, rgba(77,159,255,0.13), transparent 65%)" }}
    />
    <div className="relative max-w-[1200px] mx-auto grid md:grid-cols-2 gap-14 items-center">
      <Reveal>
        <SectionLabel>{rt.booking.label}</SectionLabel>
        <h2 className="h1-display text-[clamp(36px,4.6vw,60px)] mb-10">
          {rt.booking.h2a}<span className="accent">{rt.booking.h2b}</span>
        </h2>
        <ul className="space-y-4">
          {rt.booking.bullets.map((b, i) => (
            <li key={i} className="flex gap-3 items-start text-white/65 text-[15px]">
              <span className="accent font-black shrink-0">✓</span>{b}
            </li>
          ))}
        </ul>
      </Reveal>
      <Reveal delay={0.15}>
        <div className="glass-card p-3 md:p-4 overflow-hidden">
          <div className="mono accent text-[10px] tracking-[0.35em] px-3 pt-3 mb-2">{rt.booking.cardLabel}</div>
          <iframe
            src={BOOKING_WIDGET_URL}
            title="Reserva tu conversación con Plinio"
            id="r7SmyGC7c18FN9z1W4vS_booking"
            data-testid="booking-widget-iframe"
            scrolling="no"
            className="w-full rounded"
            style={{ border: "none", minHeight: "760px", background: "transparent" }}
          />
        </div>
      </Reveal>
    </div>
  </section>
);
